import React, { useState, useMemo, FC } from 'react';
import type { AvailabilityStatus, DayOfWeek, ScheduleEntry } from '../../types';
import { ALL_DAYS } from '../../types';
import { getWeekStartDate, getWeekDates, formatDateYYYYMMDD, formatDayAndDate, formatDateRange, getDayOfWeekFromDate } from '../../utils/dateUtils';
import { DAY_INDEX_MAP } from '../../types';
import { Icons } from '../ui/Icons';
import Popover from '../ui/Popover';

interface AvailabilityCalendarProps {
    availability: { [key in DayOfWeek]: AvailabilityStatus };
    availabilityOverrides: { [date: string]: AvailabilityStatus };
    weekStartDay: DayOfWeek;
    onOverridesChange: (overrides: { [date: string]: AvailabilityStatus }) => void;
    onAvailabilityChange: (day: DayOfWeek, status: AvailabilityStatus) => void;
    variant: 'overrides' | 'defaults';
    themeStyle?: 'admin' | 'staff';
    scheduleForUser?: { [date: string]: ScheduleEntry };
}

const adminStatusStyles: { [key in AvailabilityStatus]: { bg: string; text: string; border: string; selected: string; unselected: string; } } = {
    Available: { bg: 'bg-green-100 dark:bg-green-900/25', text: 'text-green-800 dark:text-green-300', border: 'border-green-200', selected: 'bg-green-500 text-white shadow', unselected: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30' },
    Preferred: { bg: 'bg-blue-100 dark:bg-blue-900/25', text: 'text-blue-800 dark:text-blue-300', border: 'border-blue-200', selected: 'bg-blue-500 text-white shadow', unselected: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30' },
    Unavailable: { bg: 'bg-red-100 dark:bg-red-900/25', text: 'text-red-800 dark:text-red-300', border: 'border-red-200', selected: 'bg-red-500 text-white shadow', unselected: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30' },
};

const staffStatusStyles: { [key in AvailabilityStatus]: { bg: string; text: string; selected: string; unselected: string; } } = {
    Available: { bg: 'bg-green-100 dark:bg-green-900/25', text: 'text-green-800 dark:text-green-300', selected: 'bg-green-200 border-green-400 text-green-900 dark:bg-green-900/25 dark:text-green-200', unselected: 'bg-white dark:bg-slate-700/80 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200' },
    Preferred: { bg: 'bg-blue-100 dark:bg-blue-900/25', text: 'text-blue-800 dark:text-blue-300', selected: 'bg-blue-200 border-blue-400 text-blue-900 dark:bg-blue-900/25 dark:text-blue-200', unselected: 'bg-white dark:bg-slate-700/80 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200' },
    Unavailable: { bg: 'bg-red-100 dark:bg-red-900/25', text: 'text-red-800 dark:text-red-300', selected: 'bg-red-200 border-red-400 text-red-900 dark:bg-red-900/25 dark:text-red-200', unselected: 'bg-white dark:bg-slate-700/80 border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200' },
};

const AvailabilityCalendar: FC<AvailabilityCalendarProps> = ({ availability, availabilityOverrides, weekStartDay, onOverridesChange, onAvailabilityChange, variant, themeStyle = 'admin', scheduleForUser }) => {
    const startDayIndex = DAY_INDEX_MAP[weekStartDay];
    const [currentWeekStartDate, setCurrentWeekStartDate] = useState(() => getWeekStartDate(new Date(), startDayIndex));
    
    // Update week start when settings change
    React.useEffect(() => {
        setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));
    }, [startDayIndex]);

    const weekDates = useMemo(() => getWeekDates(currentWeekStartDate), [currentWeekStartDate]);

    const goToPreviousWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() - 7);
        return newDate;
    });

    const goToNextWeek = () => setCurrentWeekStartDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + 7);
        return newDate;
    });

    const goToToday = () => setCurrentWeekStartDate(getWeekStartDate(new Date(), startDayIndex));

    const handleOverrideChange = (dateString: string, status: AvailabilityStatus | null) => {
        const newOverrides = { ...availabilityOverrides };
        if (status === null) {
            delete newOverrides[dateString]; // Reset to default
        } else {
            newOverrides[dateString] = status;
        }
        onOverridesChange(newOverrides);
    };

    interface DayItemProps {
        date: Date;
    }

    const DayItem: FC<DayItemProps> = ({ date }) => {
        const dateString = formatDateYYYYMMDD(date);
        const dayOfWeek = getDayOfWeekFromDate(date);
        const overrideStatus = availabilityOverrides[dateString];
        const defaultStatus = (availability && availability[dayOfWeek]) || 'Available';
        const currentStatus = overrideStatus || defaultStatus;
        const { day, date: dateNum } = formatDayAndDate(date);

        const scheduleEntry = scheduleForUser?.[dateString];
        const isScheduled = scheduleEntry?.status === 'ASSIGNED' || scheduleEntry?.status === 'ON_CALL';

        const PopoverContent = ({ close }: { close: () => void }) => (
            <div className="w-48">
                <div className="p-2 font-semibold text-gray-700 dark:text-gray-200 border-b dark:border-slate-700 text-center">{day}, {dateNum}</div>
                <div className="p-1">
                    {(['Available', 'Preferred', 'Unavailable'] as AvailabilityStatus[]).map(s => (
                        <button key={s} onClick={() => { handleOverrideChange(dateString, s); close(); }}
                            className={`w-full text-left px-3 py-2 my-0.5 text-sm rounded-md font-medium hover:opacity-90 ${adminStatusStyles[s].bg} ${adminStatusStyles[s].text}`}>
                            Set as {s}
                        </button>
                    ))}
                    {overrideStatus && (
                         <button onClick={() => { handleOverrideChange(dateString, null); close(); }}
                            className="w-full text-left px-3 py-2 mt-1.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2 border-t dark:border-slate-700">
                            Reset to Default
                        </button>
                    )}
                </div>
            </div>
        );

        if (themeStyle === 'staff') {
            const staffStyle = staffStatusStyles[currentStatus];
            return (
                 <li className={`flex items-center justify-between p-3 rounded-lg shadow-sm ${staffStyle.bg} ${overrideStatus ? 'ring-2 ring-indigo-500' : ''}`}>
                    <div className="flex items-center gap-4">
                        <div className="text-center w-12 flex-shrink-0">
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">{day}</p>
                            <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">{dateNum}</p>
                        </div>
                        <div className="font-medium text-sm">
                            <p className={staffStyle.text}>{currentStatus}</p>
                            {overrideStatus && <p className="text-xs text-indigo-600 dark:text-indigo-400 font-semibold">Override</p>}
                        </div>
                    </div>
                    <div className="relative">
                        <Popover content={<PopoverContent close={() => {}} />}>
                            <button
                                disabled={isScheduled}
                                title={isScheduled ? "Cannot change availability on a scheduled day." : "Change availability"}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Icons.Edit className="w-4 h-4" />
                                <span>Change</span>
                            </button>
                        </Popover>
                         {isScheduled && (
                            <div className="absolute -top-1.5 -right-1.5" title="This day is locked because you are scheduled.">
                                <Icons.Lock className="w-4 h-4 text-gray-500 bg-gray-200 dark:bg-slate-600 dark:text-gray-300 rounded-full p-0.5" />
                            </div>
                        )}
                    </div>
                </li>
            );
        }

        // Admin style (default grid cell)
        const adminStyle = adminStatusStyles[currentStatus];
        const dayContent = (
            <div className={`relative p-2 rounded-lg border-2 h-full flex flex-col justify-between ${isScheduled ? 'cursor-not-allowed' : 'cursor-pointer'} ${adminStyle.bg} ${overrideStatus ? 'border-indigo-400' : 'border-transparent'}`}>
                {isScheduled && (
                    <div className="absolute top-1 right-1" title="This day is locked because the staff member is scheduled.">
                        <Icons.Lock className="w-4 h-4 text-gray-500 dark:text-gray-300" />
                    </div>
                )}
                <div className="flex justify-between items-center">
                    <p className="text-xs font-bold text-gray-600 dark:text-gray-400 uppercase">{day}</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">{dateNum}</p>
                </div>
                <div>
                    <p className={`text-xs font-bold px-2 py-0.5 rounded-full inline-block ${adminStyle.bg} ${adminStyle.text}`}>
                        {currentStatus}
                    </p>
                    {overrideStatus && <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-semibold">Override</p>}
                </div>
            </div>
        );
        
        return isScheduled ? dayContent : <Popover content={<PopoverContent close={() => {}} />}>{dayContent}</Popover>;
    };

    if (variant === 'overrides') {
        const weekNav = (
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                    <button type="button" onClick={goToPreviousWeek} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300"><Icons.ChevronLeft className="h-5 w-5" /></button>
                    <button type="button" onClick={goToNextWeek} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300"><Icons.ChevronRight className="h-5 w-5" /></button>
                    <button type="button" onClick={goToToday} className="px-3 py-1 text-sm font-medium border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200">Today</button>
                </div>
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {formatDateRange(currentWeekStartDate)}
                </h3>
            </div>
        );

        if (themeStyle === 'staff') {
             return (
                <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Tap 'Change' to set a date-specific override. Days with scheduled shifts are locked.</p>
                    {weekNav}
                    <ul className="space-y-2">
                        {weekDates.map(date => <DayItem key={date.toISOString()} date={date} />)}
                    </ul>
                </div>
            );
        }
        
        // Admin View
        return (
             <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Tap any day to set a date-specific override (e.g., for a vacation or appointment). Overridden days will have a blue border.</p>
                {weekNav}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-center">
                    {weekDates.map(date => <DayItem key={date.toISOString()} date={date} />)}
                </div>
            </div>
        );
    }
    
    if (variant === 'defaults') {
        const styles = themeStyle === 'staff' ? staffStatusStyles : adminStatusStyles;
        return (
            <div>
                 <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Set your recurring availability. This is a guideline for future schedules and can be changed at any time.</p>
                <div className="space-y-3">
                    {ALL_DAYS.map(day => {
                        return (
                            <div key={day} className="grid grid-cols-4 items-center gap-4">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    {day}
                                </label>
                                <div className="col-span-3 flex items-center justify-start space-x-2">
                                    {(['Available', 'Preferred', 'Unavailable'] as AvailabilityStatus[]).map(status => (
                                        <button
                                            type="button" key={status} onClick={() => onAvailabilityChange(day, status)}
                                            title={`Set default to ${status}`}
                                            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-150 border focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                                                (availability && availability[day] === status) ? styles[status].selected : styles[status].unselected
                                            }`}>
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        );
    }

    return null;
};

export default AvailabilityCalendar;
